import { useState, useEffect } from 'react';
import { X, Calendar, CreditCard, CheckCircle, XCircle, Clock, Mail, User, Building2, AlertCircle, FileText, ExternalLink, Download, Eye, Database, Upload, Loader, UserPlus, Key } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { FinancialReportsSection } from './FinancialReportsSection';

interface UserDetailsModalProps {
  userId: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface UserDetails {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  role: string;
  trial_ends_at: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  current_plan: string | null;
  selected_metier_module: string | null;
  is_metier: boolean;
  is_pilotage: boolean;
  is_daf: boolean;
  is_associations: boolean;
  is_clients: boolean;
  is_funding: boolean;
}

interface Invoice {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  paid: boolean;
}

interface Backup {
  id: string;
  backup_type: string;
  created_at: string;
  notes: string;
}

export function UserDetailsModal({ userId, onClose, onUpdate }: UserDetailsModalProps) {
  const { impersonateUser } = useAuth();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [newTrialDate, setNewTrialDate] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [restoringBackupId, setRestoringBackupId] = useState<string | null>(null);
  const [isEligibleForReports, setIsEligibleForReports] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    fetchBackups();
    checkReportEligibility();
  }, [userId]);

  async function fetchUserDetails() {
    try {
      const { data, error } = await supabase.rpc('get_all_users_for_admin');

      if (error) throw error;

      const userData = data?.find((u: any) => u.id === userId);
      if (userData) {
        setUser(userData);

        if (userData.trial_ends_at) {
          const trialDate = new Date(userData.trial_ends_at);
          setNewTrialDate(trialDate.toISOString().split('T')[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInvoices() {
    if (!user?.stripe_customer_id) {
      alert('Aucun ID client Stripe trouvé');
      return;
    }

    setInvoicesLoading(true);
    setShowInvoices(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-invoice?user_id=${userId}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des factures');
      }

      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Erreur lors de la récupération des factures');
    } finally {
      setInvoicesLoading(false);
    }
  }

  async function updateTrialDate() {
    if (!newTrialDate) return;

    setActionLoading(true);
    try {
      const trialEndsAt = new Date(newTrialDate);
      trialEndsAt.setHours(23, 59, 59, 999);

      const { error } = await supabase
        .from('user_profiles')
        .update({ trial_ends_at: trialEndsAt.toISOString() })
        .eq('id', userId);

      if (error) throw error;

      await fetchUserDetails();
      onUpdate();
    } catch (error) {
      console.error('Error updating trial date:', error);
      alert('Erreur lors de la mise à jour de la période d\'essai');
    } finally {
      setActionLoading(false);
    }
  }

  async function extendTrial(days: number) {
    setActionLoading(true);
    try {
      const currentTrialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : new Date();
      const newTrialEnd = new Date(currentTrialEnd);
      newTrialEnd.setDate(newTrialEnd.getDate() + days);

      const { error } = await supabase
        .from('user_profiles')
        .update({ trial_ends_at: newTrialEnd.toISOString() })
        .eq('id', userId);

      if (error) throw error;

      await fetchUserDetails();
      onUpdate();
    } catch (error) {
      console.error('Error extending trial:', error);
      alert('Erreur lors de l\'extension de l\'essai');
    } finally {
      setActionLoading(false);
    }
  }

  async function updateSubscriptionStatus(status: 'active' | 'suspended' | 'cancelled') {
    setActionLoading(true);
    try {
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingSub) {
        const { error } = await supabase
          .from('subscriptions')
          .update({ status, updated_at: new Date().toISOString() })
          .eq('user_id', userId);

        if (error) throw error;
      } else {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('current_plan')
          .eq('id', userId)
          .maybeSingle();

        const planType = profile?.current_plan || 'essentiel';

        const { error } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            status,
            plan_type: planType,
          });

        if (error) throw error;
      }

      await fetchUserDetails();
      onUpdate();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Erreur lors de la mise à jour de l\'abonnement');
    } finally {
      setActionLoading(false);
    }
  }

  async function deleteUser() {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('delete_user_data', { target_user_id: userId });

      if (error) throw error;

      alert('Utilisateur supprimé avec succès');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression de l\'utilisateur');
    } finally {
      setActionLoading(false);
    }
  }

  async function changeUserPassword() {
    if (!newPassword) {
      alert('Veuillez saisir un nouveau mot de passe');
      return;
    }

    if (newPassword.length < 8) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir changer le mot de passe de cet utilisateur ?`)) {
      return;
    }

    setChangingPassword(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-change-password`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: userId,
          new_password: newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors du changement de mot de passe');
      }

      alert('Mot de passe modifié avec succès');
      setNewPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  }

  async function fetchBackups() {
    setBackupsLoading(true);
    setShowBackups(true);
    try {
      const { data, error } = await supabase
        .from('data_backups')
        .select('id, backup_type, created_at, notes')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      setBackups(data || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      alert('Erreur lors de la récupération des sauvegardes');
    } finally {
      setBackupsLoading(false);
    }
  }

  async function checkReportEligibility() {
    console.log('[UserDetailsModal] Checking report eligibility for userId:', userId);
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('is_pilotage, is_daf')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;

      const isEligible = !!(profile?.is_pilotage || profile?.is_daf);
      console.log('[UserDetailsModal] Eligibility result:', { profile, isEligible });
      setIsEligibleForReports(isEligible);
    } catch (error) {
      console.error('Error checking report eligibility:', error);
      setIsEligibleForReports(false);
    }
  }

  async function handleRestoreBackup(backupId: string) {
    if (!confirm('Êtes-vous sûr de vouloir restaurer cette sauvegarde ? Toutes les données actuelles de cet utilisateur seront remplacées par celles de la sauvegarde.')) {
      return;
    }

    setRestoringBackupId(backupId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/restore-backup`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backup_id: backupId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la restauration');
      }

      alert('Sauvegarde restaurée avec succès');
      onUpdate();
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('Erreur lors de la restauration de la sauvegarde');
    } finally {
      setRestoringBackupId(null);
    }
  }

  async function handleCreateManualBackup() {
    setActionLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Non authentifié');

      const { error } = await supabase.rpc('create_user_backup', {
        target_user_id: userId,
        backup_type_param: 'manual',
        created_by_param: currentUser.id,
        notes_param: 'Sauvegarde manuelle créée depuis la fiche utilisateur'
      });

      if (error) throw error;

      alert('Sauvegarde créée avec succès');
      await fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Erreur lors de la création de la sauvegarde');
    } finally {
      setActionLoading(false);
    }
  }

  async function convertToBetaFounder(resetPassword: boolean) {
    const action = resetPassword ? 'convertir cet utilisateur en Beta Founder ET réinitialiser son mot de passe' : 'convertir cet utilisateur en Beta Founder';
    const details = resetPassword
      ? '\n\nActions qui seront effectuées:\n- Passage au statut Beta Founder\n- Activation de tous les modules premium\n- Extension de la période d\'essai à 150 jours\n- Génération d\'un nouveau mot de passe temporaire\n- Envoi d\'un email avec les nouveaux identifiants'
      : '\n\nActions qui seront effectuées:\n- Passage au statut Beta Founder\n- Activation de tous les modules premium\n- Extension de la période d\'essai à 150 jours\n- Envoi d\'un email de notification\n- Le mot de passe reste inchangé';

    if (!confirm(`Êtes-vous sûr de vouloir ${action} ?${details}`)) {
      return;
    }

    setActionLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        alert('Session expirée. Veuillez vous reconnecter.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/convert-to-beta-founder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            resetPassword: resetPassword
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la conversion');
      }

      const successMessage = resetPassword
        ? `Utilisateur converti en Beta Founder avec succès!\n\nUn email a été envoyé avec les nouveaux identifiants.`
        : `Utilisateur converti en Beta Founder avec succès!\n\nUn email de notification a été envoyé.`;

      alert(successMessage);
      await fetchUserDetails();
      onUpdate();
    } catch (error) {
      console.error('Error converting to beta founder:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setActionLoading(false);
    }
  }

  async function updateUserPermissions(plan: string, module: string | null) {
    setActionLoading(true);
    try {
      console.log('[UserDetailsModal] Updating permissions:', { userId, plan, module });

      const updates: any = {
        current_plan: plan,
        selected_metier_module: module,
        is_metier: ['metier', 'pilotage', 'daf'].includes(plan),
        is_pilotage: ['pilotage', 'daf'].includes(plan),
        is_daf: plan === 'daf',
        is_associations: module === 'associations',
        is_clients: module === 'pme',
        is_funding: module === 'agri'
      };

      console.log('[UserDetailsModal] Updates to apply:', updates);

      const { error, data } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select();

      if (error) {
        console.error('[UserDetailsModal] Error updating user_profiles:', error);
        throw error;
      }

      console.log('[UserDetailsModal] user_profiles updated:', data);

      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id, status')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingSub) {
        await supabase
          .from('subscriptions')
          .update({
            status: 'active',
            plan_type: plan,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        console.log('[UserDetailsModal] Subscription updated to active with plan:', plan);
      } else {
        await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            status: 'active',
            plan_type: plan,
          });
        console.log('[UserDetailsModal] Subscription created with plan:', plan);
      }

      if (module) {
        const { data: moduleData, error: moduleError } = await supabase
          .from('modules_metiers')
          .select('id')
          .eq('slug', module)
          .single();

        if (moduleError) {
          console.error('[UserDetailsModal] Error fetching module:', moduleError);
          throw moduleError;
        }

        console.log('[UserDetailsModal] Module data:', moduleData);

        if (moduleData) {
          const { error: deleteError } = await supabase
            .from('client_modules')
            .delete()
            .eq('user_id', userId);

          if (deleteError) {
            console.error('[UserDetailsModal] Error deleting client_modules:', deleteError);
          }

          const { error: insertError, data: insertData } = await supabase
            .from('client_modules')
            .insert({
              user_id: userId,
              module_id: moduleData.id,
              active: true,
              activated_at: new Date().toISOString()
            })
            .select();

          if (insertError) {
            console.error('[UserDetailsModal] Error inserting client_modules:', insertError);
            throw insertError;
          }

          console.log('[UserDetailsModal] client_modules inserted:', insertData);
        }
      } else {
        const { error: deleteError } = await supabase
          .from('client_modules')
          .delete()
          .eq('user_id', userId);

        if (deleteError) {
          console.error('[UserDetailsModal] Error deleting client_modules:', deleteError);
        }
      }

      await fetchUserDetails();
      onUpdate();
      alert('Permissions et abonnement mis à jour avec succès');
    } catch (error) {
      console.error('[UserDetailsModal] Error updating permissions:', error);
      alert(`Erreur lors de la mise à jour des permissions: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleAccessUserSpace() {
    setActionLoading(true);
    try {
      console.log('[UserDetailsModal] Starting impersonation for userId:', userId);
      const { error } = await impersonateUser(userId);
      console.log('[UserDetailsModal] Impersonation result:', { error });

      if (error) {
        const errorMessage = error.error || error.message || JSON.stringify(error);
        console.error('[UserDetailsModal] Impersonation error:', errorMessage);
        alert(`Erreur lors de l'accès à l'espace utilisateur: ${errorMessage}`);
        setActionLoading(false);
      }
    } catch (error) {
      console.error('[UserDetailsModal] Exception during impersonation:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`Erreur lors de l'accès à l'espace utilisateur: ${errorMessage}`);
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const isTrialActive = user.trial_ends_at && new Date(user.trial_ends_at) > new Date();
  const daysRemaining = user.trial_ends_at
    ? Math.ceil((new Date(user.trial_ends_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Fiche utilisateur</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Bouton d'accès à l'espace utilisateur */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <button
              onClick={handleAccessUserSpace}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              <Eye size={20} />
              Accéder à l'espace de cet utilisateur
            </button>
            <p className="text-xs text-gray-600 mt-2 text-center">
              Vous pourrez voir et gérer les données de cet utilisateur comme si vous étiez connecté à sa place
            </p>
          </div>

          {/* Informations générales */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <User className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Nom complet</div>
                  <div className="font-medium text-gray-900">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : 'Non renseigné'}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div className="font-medium text-gray-900">{user.email}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Building2 className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Structure</div>
                  <div className="font-medium text-gray-900">{user.company || 'Non renseignée'}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Inscrit le</div>
                  <div className="font-medium text-gray-900">
                    {new Date(user.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Dernière connexion</div>
                  <div className="font-medium text-gray-900">
                    {user.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : 'Jamais'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Changement de mot de passe */}
          <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Key size={20} className="text-yellow-600" />
                Réinitialiser le mot de passe
              </h3>
              {!showPasswordSection && (
                <button
                  onClick={() => setShowPasswordSection(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                >
                  Changer le mot de passe
                </button>
              )}
            </div>

            {showPasswordSection && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nouveau mot de passe (minimum 8 caractères)
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Saisir le nouveau mot de passe"
                    disabled={changingPassword}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={changeUserPassword}
                    disabled={changingPassword || !newPassword}
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {changingPassword ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        Modification en cours...
                      </>
                    ) : (
                      <>
                        <Key size={20} />
                        Confirmer le changement
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordSection(false);
                      setNewPassword('');
                    }}
                    disabled={changingPassword}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Annuler
                  </button>
                </div>
                <p className="text-xs text-gray-600">
                  Le mot de passe sera immédiatement modifié. L'utilisateur pourra se connecter avec ce nouveau mot de passe.
                </p>
              </div>
            )}

            {!showPasswordSection && (
              <p className="text-sm text-gray-600">
                Permet de définir un nouveau mot de passe pour cet utilisateur en cas d'oubli ou de demande.
              </p>
            )}
          </div>

          {/* Plan et Module Métier */}
          <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan et Module Métier</h3>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan actuel
                  </label>
                  <select
                    value={user.current_plan || ''}
                    onChange={(e) => {
                      const newPlan = e.target.value || null;
                      const needsModule = ['metier', 'pilotage', 'daf'].includes(newPlan!);
                      const currentModule = needsModule ? (user.selected_metier_module || 'agri') : null;
                      if (confirm(`Changer le plan vers "${newPlan}" ?`)) {
                        updateUserPermissions(newPlan!, currentModule);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={actionLoading}
                  >
                    <option value="">Aucun plan</option>
                    <option value="essentiel">Pack Essentiel</option>
                    <option value="metier">Pack Métier</option>
                    <option value="pilotage">Pack Pilotage</option>
                    <option value="daf">Pack DAF</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Module métier
                  </label>
                  <select
                    value={user.selected_metier_module || ''}
                    onChange={(e) => {
                      const newModule = e.target.value || null;
                      if (confirm(`Changer le module vers "${newModule || 'aucun'}" ?`)) {
                        updateUserPermissions(user.current_plan || 'essentiel', newModule);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={actionLoading || !['metier', 'pilotage', 'daf'].includes(user.current_plan || '')}
                  >
                    <option value="">Aucun module</option>
                    <option value="agri">Agriculture</option>
                    <option value="associations">Associations</option>
                    <option value="pme">PME</option>
                  </select>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="text-sm font-medium text-gray-700 mb-2">Permissions calculées :</div>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${user.is_metier ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {user.is_metier ? '✓' : '✗'} Modules Métiers
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${user.is_pilotage ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {user.is_pilotage ? '✓' : '✗'} Rapports Auto
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${user.is_daf ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {user.is_daf ? '✓' : '✗'} Espace DAF
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Période d'essai */}
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="text-blue-600" size={20} />
              Période d'essai
            </h3>

            {user.trial_ends_at ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  {isTrialActive ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle size={16} />
                      Essai actif ({daysRemaining} jour{daysRemaining! > 1 ? 's' : ''} restant{daysRemaining! > 1 ? 's' : ''})
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <XCircle size={16} />
                      Essai expiré
                    </span>
                  )}
                  <span className="text-sm text-gray-600">
                    Expire le {new Date(user.trial_ends_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => extendTrial(7)}
                    disabled={actionLoading}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    +7 jours
                  </button>
                  <button
                    onClick={() => extendTrial(14)}
                    disabled={actionLoading}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    +14 jours
                  </button>
                  <button
                    onClick={() => extendTrial(30)}
                    disabled={actionLoading}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    +30 jours
                  </button>
                </div>

                <div className="flex gap-2">
                  <input
                    type="date"
                    value={newTrialDate}
                    onChange={(e) => setNewTrialDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={updateTrialDate}
                    disabled={actionLoading || !newTrialDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Définir
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Aucune période d'essai configurée</p>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={newTrialDate}
                    onChange={(e) => setNewTrialDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={updateTrialDate}
                    disabled={actionLoading || !newTrialDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Activer l'essai
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Abonnement */}
          <div className="bg-green-50 rounded-lg p-6 border border-green-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="text-green-600" size={20} />
              Abonnement Stripe
            </h3>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4">
              <p className="text-sm text-blue-800">
                Pour configurer manuellement un plan et un module, utilisez la section "Plan et Module Métier" ci-dessus.
                Cette section affiche les informations de l'abonnement Stripe.
              </p>
            </div>

            {user.subscription_status ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4">
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                        user.subscription_status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : user.subscription_status === 'suspended'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.subscription_status === 'active' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      {user.subscription_status === 'active' ? 'Actif' :
                       user.subscription_status === 'suspended' ? 'Suspendu' : 'Annulé'}
                    </span>
                    {user.subscription_plan && (
                      <span className="text-sm font-medium text-gray-700">
                        Plan {user.subscription_plan}
                      </span>
                    )}
                  </div>

                  {user.current_period_end && user.subscription_status === 'active' && (
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-green-200">
                      <Calendar className="text-green-600" size={18} />
                      <div>
                        <div className="text-xs text-gray-500">Prochain renouvellement</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {new Date(user.current_period_end).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {user.stripe_subscription_id && (
                  <div className="text-sm bg-white px-4 py-3 rounded-lg border border-green-200">
                    <div className="text-gray-500">ID Stripe: <span className="font-mono text-gray-700">{user.stripe_subscription_id}</span></div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {user.stripe_customer_id && (
                    <button
                      onClick={fetchInvoices}
                      disabled={invoicesLoading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <FileText size={16} />
                      {invoicesLoading ? 'Chargement...' : 'Voir les factures'}
                    </button>
                  )}
                  {user.subscription_status !== 'active' && (
                    <button
                      onClick={() => updateSubscriptionStatus('active')}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Activer
                    </button>
                  )}
                  {user.subscription_status !== 'suspended' && (
                    <button
                      onClick={() => updateSubscriptionStatus('suspended')}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                    >
                      Suspendre
                    </button>
                  )}
                  {user.subscription_status !== 'cancelled' && (
                    <button
                      onClick={() => updateSubscriptionStatus('cancelled')}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                  )}
                </div>

                {showInvoices && invoices.length > 0 && (
                  <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                    <div className="px-4 py-3 bg-green-100 border-b border-green-200">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <FileText size={18} className="text-green-600" />
                        Factures Stripe
                      </h4>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm text-gray-600">
                                {invoice.number || invoice.id}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                invoice.paid
                                  ? 'bg-green-100 text-green-800'
                                  : invoice.status === 'open'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {invoice.paid ? 'Payée' : invoice.status === 'open' ? 'En attente' : invoice.status}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              {new Date(invoice.created * 1000).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })} - {invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {invoice.invoice_pdf && (
                              <a
                                href={invoice.invoice_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                              >
                                <Download size={14} />
                                PDF
                              </a>
                            )}
                            {invoice.hosted_invoice_url && (
                              <a
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                              >
                                <ExternalLink size={14} />
                                Voir
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showInvoices && invoices.length === 0 && !invoicesLoading && (
                  <div className="bg-white rounded-lg border border-green-200 p-4 text-center text-gray-500">
                    Aucune facture trouvée
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Aucun abonnement actif</p>
                <button
                  onClick={() => updateSubscriptionStatus('active')}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Créer un abonnement manuel
                </button>
              </div>
            )}
          </div>

          {/* Rapports Financiers */}
          <FinancialReportsSection userId={userId} isEligible={isEligibleForReports} />

          {/* Sauvegardes */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="text-gray-600" size={20} />
              Sauvegardes
            </h3>

            <div className="space-y-4">
              <button
                onClick={handleCreateManualBackup}
                disabled={actionLoading}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Download size={16} />
                Créer une sauvegarde manuelle
              </button>

              {showBackups && (
                <>
                  {backupsLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader className="w-6 h-6 animate-spin text-gray-600" />
                    </div>
                  ) : backups.length > 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Database size={18} className="text-gray-600" />
                          Sauvegardes disponibles ({backups.length})
                        </h4>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {backups.map((backup) => (
                          <div key={backup.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  backup.backup_type === 'automatic'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {backup.backup_type === 'automatic' ? 'Automatique' : 'Manuelle'}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {new Date(backup.created_at).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              {backup.notes && (
                                <div className="text-xs text-gray-500 mt-1">{backup.notes}</div>
                              )}
                            </div>
                            <button
                              onClick={() => handleRestoreBackup(backup.id)}
                              disabled={restoringBackupId === backup.id}
                              className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                            >
                              {restoringBackupId === backup.id ? (
                                <>
                                  <Loader size={14} className="animate-spin" />
                                  Restauration...
                                </>
                              ) : (
                                <>
                                  <Upload size={14} />
                                  Restaurer
                                </>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-gray-200 p-4 text-center text-gray-500">
                      Aucune sauvegarde disponible
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Conversion Beta Founder */}
          {user.role !== 'beta_founder' && (
            <div className="bg-green-50 rounded-lg p-6 border border-green-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UserPlus className="text-green-600" size={20} />
                Conversion Beta Founder
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Transformez cet utilisateur en Beta Founder avec accès complet à toutes les fonctionnalités premium pour 150 jours.
              </p>

              <div className="bg-white rounded-lg p-4 border border-green-200 mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Ce qui sera activé :</h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                  <li>Statut Beta Founder</li>
                  <li>Tous les modules premium (Pilotage, DAF, Modules Métiers)</li>
                  <li>Période d'essai de 150 jours (5 mois)</li>
                  <li>Support prioritaire</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => convertToBetaFounder(false)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <UserPlus size={16} />
                  Convertir (garder le mot de passe)
                </button>
                <button
                  onClick={() => convertToBetaFounder(true)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <UserPlus size={16} />
                  Convertir (+ nouveau mot de passe)
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-3 text-center">
                L'utilisateur recevra un email de notification dans les deux cas
              </p>
            </div>
          )}

          {user.role === 'beta_founder' && (
            <div className="bg-green-50 rounded-lg p-6 border border-green-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                Cet utilisateur est déjà Beta Founder
              </h3>
              <p className="text-sm text-gray-600">
                L'utilisateur dispose déjà du statut Beta Founder avec accès complet à toutes les fonctionnalités.
              </p>
            </div>
          )}

          {/* Actions dangereuses */}
          <div className="bg-red-50 rounded-lg p-6 border border-red-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="text-red-600" size={20} />
              Zone dangereuse
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              La suppression d'un utilisateur est irréversible. Toutes ses données seront définitivement perdues.
            </p>
            <button
              onClick={deleteUser}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Supprimer l'utilisateur
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
